import React, { FunctionComponent } from 'react';

import Modal from '../Modal/Modal';

import { gql } from '@apollo/client';
import styles from './DonationReminderModal.scss';
import { useTranslation } from 'react-i18next';
import { AddToCalendarButton } from '../AddToCalendarButton/AddToCalendarButton';
import { CalendarType } from '../AddToCalendarButton/helpers/addToCalendar';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { isCharity } from '../../utils/typeGuards';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';
import { DonationReminderModalFragment } from '../../../types/__generated__';
import { usePageMeta } from '../../hooks';

// OTG 2023 - Tom Hedges

const FRAGMENT = buildQuery(gql`
  fragment DonationReminderModal on Page {
    id
    targetWithCurrency {
      value
      currencyCode
    }
  }
`);

type DonationReminderModalProps = {
  onClose: () => void;
};

export const DonationReminderModal: FunctionComponent<DonationReminderModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data: basePageData } = useBasePageQuery();
  const { currentUrl } = usePageMeta();

  const { loading, data = null } = usePageQuery<DonationReminderModalFragment>(
    FRAGMENT,
    {
      notifyOnNetworkStatusChange: true,
    },
  );

  if (
    loading ||
    !basePageData?.page?.owner ||
    !basePageData?.page?.relationships
  ) {
    return null;
  }

  const { owner, relationships } = basePageData?.page;

  const ownerName = owner.name;
  const charities = relationships?.beneficiaries.nodes.filter(isCharity);
  const charityName = charities[0].name;
  const currencyCode = data?.targetWithCurrency?.currencyCode;
  const targetAmount = data?.targetWithCurrency?.value / 100;
  const event = relationships.events?.nodes[0];
  const eventName = event?.title;
  const rawStartDate = event?.startDate;
  const rawEndDate = event?.endDate;

  if (
    !ownerName ||
    !charityName ||
    !eventName ||
    !currencyCode ||
    !targetAmount ||
    !rawStartDate
  ) {
    return null;
  }

  const startDate = new Date(rawStartDate);
  const endDate = rawEndDate ? new Date(rawEndDate) : undefined;

  return (
    <Modal
      onCloseRequest={onClose}
      className={styles.modal}
      modalTitle={t('donationReminder.title')}
      removeScrollbar
      displayCloseButton
    >
      <div className="jg-space-mhn">
        <div>
          <h2 className="jg-h2">{t('donationReminder.heading')}</h2>
          <p className={`jg-space-mbsm ${styles.text}`}>
            {t('donationReminder.text', { ownerName })}
          </p>
        </div>
      </div>
      <div>
        <AddToCalendarButton
          ownerName={ownerName}
          calendarType={CalendarType.Apple}
          charityName={charityName}
          eventName={eventName}
          currencyCode={currencyCode}
          url={currentUrl}
          targetAmount={targetAmount}
          startTime={startDate}
          endTime={endDate}
        />
        <AddToCalendarButton
          ownerName={ownerName}
          calendarType={CalendarType.Google}
          charityName={charityName}
          eventName={eventName}
          currencyCode={currencyCode}
          url={currentUrl}
          targetAmount={targetAmount}
          startTime={startDate}
          endTime={endDate}
        />
        <AddToCalendarButton
          ownerName={ownerName}
          calendarType={CalendarType.Outlook}
          charityName={charityName}
          eventName={eventName}
          currencyCode={currencyCode}
          url={currentUrl}
          targetAmount={targetAmount}
          startTime={startDate}
          endTime={endDate}
        />
        <AddToCalendarButton
          ownerName={ownerName}
          calendarType={CalendarType.OutlookWeb}
          buttonText={t('donationReminder.outlookWeb')}
          charityName={charityName}
          eventName={eventName}
          currencyCode={currencyCode}
          url={currentUrl}
          targetAmount={targetAmount}
          startTime={startDate}
          endTime={endDate}
        />
      </div>
    </Modal>
  );
};
